import { buildDirectoryTree, type SharedFolderManifestPlaintext } from '../crypto/sharedFolderManifest';
import { applyI18n } from '../i18n';
import { dialogHelpTopic } from '../help/dialogHelpTopics';
import { bindModalDismiss, createModalShell } from './modalDialogShell';

export async function showFilesMoveDialog(input: {
  manifest: SharedFolderManifestPlaintext;
  currentPath: string;
  excludePath?: string;
}): Promise<string | null> {
  return new Promise((resolve) => {
    const shell = createModalShell({
      titleKey: 'files.owner.moveTo',
      helpTopic: dialogHelpTopic('filesMove'),
      className: 'files-move-dialog',
    });

    shell.body.innerHTML = `
      <p class="muted" data-i18n="files.owner.moveToHint"></p>
      <div class="files-move-dialog__tree" id="files-move-dialog-tree"></div>
    `;

    const cancelButton = document.createElement('button');
    cancelButton.type = 'button';
    cancelButton.className = 'button ghost';
    cancelButton.id = 'files-move-cancel';
    cancelButton.innerHTML = '<span data-i18n="common.cancel"></span>';

    const confirmButton = document.createElement('button');
    confirmButton.type = 'button';
    confirmButton.className = 'button primary';
    confirmButton.id = 'files-move-confirm';
    confirmButton.innerHTML = '<span data-i18n="common.confirm"></span>';

    shell.actions.append(cancelButton, confirmButton);
    document.body.append(shell.backdrop);
    applyI18n(shell.dialog);

    let selectedPath = input.currentPath;
    const treeHost = shell.body.querySelector('#files-move-dialog-tree') as HTMLElement;
    const tree = buildDirectoryTree(input.manifest);

    const finish = (value: string | null) => {
      unbindDismiss();
      shell.backdrop.remove();
      resolve(value);
    };

    const unbindDismiss = bindModalDismiss({
      backdrop: shell.backdrop,
      closeButton: shell.closeButton,
      onClose: () => finish(null),
    });

    const renderTree = () => {
      treeHost.replaceChildren();

      const rootButton = document.createElement('button');
      rootButton.type = 'button';
      rootButton.className = `files-move-dialog__node${selectedPath === '' ? ' files-move-dialog__node--active' : ''}`;
      rootButton.textContent = '/';
      rootButton.addEventListener('click', () => {
        selectedPath = '';
        renderTree();
      });
      treeHost.append(rootButton);

      const list = document.createElement('ul');
      list.className = 'files-move-dialog__list';
      for (const child of tree.children) {
        appendNode(list, child, 0);
      }
      treeHost.append(list);
    };

    const appendNode = (
      list: HTMLElement,
      node: ReturnType<typeof buildDirectoryTree>['children'][number],
      depth: number,
    ) => {
      if (input.excludePath && (node.path === input.excludePath || node.path.startsWith(`${input.excludePath}/`))) {
        return;
      }

      const item = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `files-move-dialog__node${selectedPath === node.path ? ' files-move-dialog__node--active' : ''}`;
      button.style.paddingLeft = `${depth + 1}rem`;
      button.textContent = node.name;
      button.addEventListener('click', () => {
        selectedPath = node.path;
        renderTree();
      });
      item.append(button);
      list.append(item);

      if (node.children.length > 0) {
        const childList = document.createElement('ul');
        childList.className = 'files-move-dialog__list';
        for (const child of node.children) {
          appendNode(childList, child, depth + 1);
        }
        item.append(childList);
      }
    };

    renderTree();

    cancelButton.addEventListener('click', () => finish(null));
    confirmButton.addEventListener('click', () => finish(selectedPath));
  });
}
